import type { CSSProperties, RefObject } from 'react';
import classNames from 'classnames';

import { CurrencySymbolEnum } from '../../../../lib/enums';
import lang from '../../../../lib/language';
import { formatCompactMetricValue } from '../../../../lib/util/DataHelpers';

import styles from './StockColumnTooltip.module.scss';

type TooltipSide = 'left' | 'right';

type Props = {
  primaryMetric: number;
  secondaryMetric: number;
  volume: number;
  marketCap: number;
  isDividend: boolean;
  currencySymbol?: string;
  visible: boolean;
  side: TooltipSide;
  top: number;
  left: number;
  pointerTop: number;
  tooltipRef: RefObject<HTMLDivElement>;
};

export const StockColumnTooltip = ({
  primaryMetric,
  secondaryMetric,
  volume,
  marketCap,
  isDividend,
  currencySymbol = CurrencySymbolEnum.USD,
  visible,
  side,
  top,
  left,
  pointerTop,
  tooltipRef,
}: Props) => {
  const tooltipStyle = {
    top: `${top}px`,
    left: `${left}px`,
    '--tooltip-pointer-top': `${pointerTop}px`,
  } as CSSProperties;

  return (
    <div
      className={classNames(styles.tooltip, {
        [styles.visible]: visible,
        [styles.leftSide]: side === 'left',
        [styles.rightSide]: side === 'right',
      })}
      style={tooltipStyle}
      ref={tooltipRef}
    >
      <span className={styles.textWrapper}>
        <p className={styles.title}>
          {isDividend ? lang.commonDiscoverInfoBoxDividendRate() : lang.commonDiscoverInfoBoxRevenue()}:
        </p>
        <p className={styles.value}>
          {formatCompactMetricValue(primaryMetric, { withCurrency: true, currencySymbol })}
        </p>
      </span>
      <span className={styles.textWrapper}>
        <p className={styles.title}>
          {isDividend ? lang.commonStockDetailsStatsPrevClose() : lang.commonDiscoverInfoBoxEps()}:
        </p>
        <p className={styles.value}>
          {formatCompactMetricValue(secondaryMetric, { withCurrency: true, currencySymbol })}
        </p>
      </span>
      <span className={styles.textWrapper}>
        <p className={styles.title}>
          {
            isDividend
              ? lang.commonDiscoverSubtitleLongTermInvestmentsTableHeader5()
              : lang.commonStockDetailsStatsAvgVolume()
          }
          :
        </p>
        <p className={styles.value}>{formatCompactMetricValue(volume)}</p>
      </span>
      <span className={styles.textWrapper}>
        <p className={styles.title}>{lang.commonDiscoverInfoBoxMarketCap()}:</p>
        <p className={styles.value}>{formatCompactMetricValue(marketCap, { withCurrency: true, currencySymbol })}</p>
      </span>
    </div>
  );
};
